// SELECT clause-splitter: walks the top-level keyword stream of a single
// SELECT-branch byte range and records the offset where each clause starts and
// ends. Compound branches (UNION / INTERSECT / EXCEPT) are split BEFORE this
// runs; this module sees one branch at a time.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: Query Structure Extractor — SELECT.

import type { ParseOptions, SqlEngine } from '../../sql-grammar/model';
import { skipContextFor } from '../classify/tokenSkip';
import type { SetOperatorKind } from '../types/queryStructure';
import { scanTopLevelKeywords, skipWsAndComments, trimRange, type TopKeyword } from './scanner';

export interface SelectClauseRanges {
  readonly selectKw: TopKeyword;
  readonly distinct?: TopKeyword;
  readonly all?: TopKeyword;
  // MSSQL `SELECT TOP n` lives between the DISTINCT/ALL and the select-list.
  readonly top?: TopKeyword;
  // T-SQL `SELECT … INTO #temp FROM …` — captured for v1 awareness, not
  // populated into QueryStructure (the read-only DML PRD owns it).
  readonly into?: TopKeyword;
  // First-token offset of each top-level clause; absent when the clause is.
  readonly fromKw?: TopKeyword;
  readonly whereKw?: TopKeyword;
  readonly groupKw?: TopKeyword; // GROUP (must be followed by BY)
  readonly havingKw?: TopKeyword;
  readonly windowKw?: TopKeyword;
  readonly orderKw?: TopKeyword; // ORDER (must be followed by BY)
  readonly limitKw?: TopKeyword;  // LIMIT / OFFSET / FETCH (whichever appears first)
  // The branch's trailing lock / output / hint clause (`tailClauseAt`). Not
  // modelled; it only ends the clause before it, so `WHERE a = 1 FOR UPDATE`
  // reads `a = 1` and a FROM item stops before `FOR UPDATE`.
  readonly tailKw?: TopKeyword;
  // First top-level set-operator (UNION / INTERSECT / EXCEPT) — null when this
  // branch is a leaf.
  readonly setOpKw?: TopKeyword;
  readonly setOpUpper?: SetOperatorKind;
}

const CLAUSE_KEYWORDS: ReadonlySet<string> = new Set([
  'FROM', 'WHERE', 'GROUP', 'HAVING', 'WINDOW', 'ORDER', 'LIMIT', 'OFFSET', 'FETCH',
]);
const SET_OPS: ReadonlySet<string> = new Set(['UNION', 'INTERSECT', 'EXCEPT']);

// The words after `FOR` that make it a row-lock clause (`FOR UPDATE`,
// `FOR NO KEY UPDATE`, `FOR SHARE`, `FOR KEY SHARE` — PostgreSQL, MySQL,
// Oracle) or a SQL Server result-format clause (`FOR XML RAW|AUTO|EXPLICIT|PATH`,
// `FOR JSON AUTO|PATH`, `FOR BROWSE`). Any other `FOR` stays part of its
// clause — SQL Server's temporal `FROM t FOR SYSTEM_TIME AS OF …` above all.
const FOR_TAILS: readonly (readonly string[])[] = [
  ['UPDATE'], ['SHARE'], ['NO', 'KEY', 'UPDATE'], ['KEY', 'SHARE'], ['BROWSE'],
  ['XML', 'RAW'], ['XML', 'AUTO'], ['XML', 'EXPLICIT'], ['XML', 'PATH'], ['JSON', 'AUTO'], ['JSON', 'PATH'],
];
const LOCK_TAIL: readonly string[] = ['IN', 'SHARE', 'MODE'];

/** Whether `kws[k]` is followed by exactly `words`, with only whitespace / comments between them. */
function followedBy(kws: readonly TopKeyword[], k: number, words: readonly string[], source: string, options: ParseOptions): boolean {
  return words.every((word, w) => {
    const prev = kws[k + w];
    const next = kws[k + w + 1];
    return next?.upper === word && trimRange(source, prev.end, next.start, options).startOffset === next.start;
  });
}

/** Whether the word at `start` follows a qualifier's `.` (`t.lock`, `"t" . lock`) — not a number's (`1. FOR UPDATE`). */
function qualifiedAt(source: string, start: number): boolean {
  let i = start - 1;
  while (i >= 0 && /\s/.test(source[i]!)) i--;
  if (source[i] !== '.') return false;
  let j = i - 1;
  while (j >= 0 && /\d/.test(source[j]!)) j--;
  return j === i - 1 || /[\w$"`\]]/.test(source[j] ?? '');
}

/**
 * Whether `kws[k]` opens the clause that ends a SELECT after its WHERE /
 * GROUP BY / ORDER BY / LIMIT: a `FOR` lock / format clause (above), MySQL's
 * `LOCK IN SHARE MODE`, or SQL Server's `OPTION (…)` query hints — the whole
 * keyword sequence, at clause level. A qualified name is a column, whatever
 * its spelling (`t.lock IN (1, 2)`, `t.for`, `t.option`), and the engine-only
 * forms are gated: `lock` / `option` are ordinary names elsewhere.
 */
function tailClauseAt(kws: readonly TopKeyword[], k: number, source: string, options: ParseOptions): boolean {
  const u = kws[k].upper;
  if (u !== 'FOR' && u !== 'LOCK' && u !== 'OPTION') return false;
  if (qualifiedAt(source, kws[k].start)) return false;
  if (u === 'FOR') return FOR_TAILS.some((words) => followedBy(kws, k, words, source, options));
  if (u === 'LOCK') return options.engine === 'mysql' && followedBy(kws, k, LOCK_TAIL, source, options);
  return options.engine === 'mssql'
    && source[skipWsAndComments(source, kws[k].end, source.length, skipContextFor(options))] === '(';
}

// `MINUS` is a set operator ONLY on Oracle; on every other engine `minus` is a
// legal column alias, so ungated recognition would corrupt-split
// `SELECT a minus FROM t`. Keep this dialect gate in lockstep with the three VQE
// scanners (extractSetOperatorBranches.ts / detectSetOperatorPrecedence.ts) so
// resolver separator indices and UI separator indices stay 1:1.
function isSetOp(upper: string, engine: SqlEngine): boolean {
  if (SET_OPS.has(upper)) return true;
  return engine === 'oracle' && upper === 'MINUS';
}

// eslint-disable-next-line complexity, max-lines-per-function
export function splitSelectClauses(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): SelectClauseRanges | null {
  const kws = scanTopLevelKeywords(source, start, end, options);
  // Locate the first SELECT keyword at depth 0.
  const selectIdx = kws.findIndex((k) => k.upper === 'SELECT');
  if (selectIdx < 0) return null;
  const selectKw = kws[selectIdx];

  let cursor = selectIdx + 1;
  let distinct: TopKeyword | undefined;
  let all: TopKeyword | undefined;
  let top: TopKeyword | undefined;
  if (cursor < kws.length && kws[cursor].upper === 'DISTINCT') { distinct = kws[cursor]; cursor++; }
  else if (cursor < kws.length && kws[cursor].upper === 'ALL') { all = kws[cursor]; cursor++; }
  if (cursor < kws.length && kws[cursor].upper === 'TOP') { top = kws[cursor]; cursor++; }

  let into: TopKeyword | undefined;
  let fromKw: TopKeyword | undefined;
  let whereKw: TopKeyword | undefined;
  let groupKw: TopKeyword | undefined;
  let havingKw: TopKeyword | undefined;
  let windowKw: TopKeyword | undefined;
  let orderKw: TopKeyword | undefined;
  let limitKw: TopKeyword | undefined;
  let tailKw: TopKeyword | undefined;
  let setOpKw: TopKeyword | undefined;
  let setOpUpper: SetOperatorKind | undefined;

  for (let k = cursor; k < kws.length; k++) {
    const u = kws[k].upper;
    // INTO at top level inside the select-list region (T-SQL SELECT INTO).
    if (!fromKw && !into && u === 'INTO') { into = kws[k]; continue; }
    if (!fromKw && u === 'FROM') { fromKw = kws[k]; continue; }
    if (!whereKw && u === 'WHERE') { whereKw = kws[k]; continue; }
    if (!groupKw && u === 'GROUP' && kws[k + 1] && kws[k + 1].upper === 'BY') {
      groupKw = kws[k]; continue;
    }
    if (!havingKw && u === 'HAVING') { havingKw = kws[k]; continue; }
    if (!windowKw && u === 'WINDOW') { windowKw = kws[k]; continue; }
    if (!orderKw && u === 'ORDER' && kws[k + 1] && kws[k + 1].upper === 'BY') {
      orderKw = kws[k]; continue;
    }
    if (!limitKw && (u === 'LIMIT' || u === 'OFFSET' || u === 'FETCH')) {
      limitKw = kws[k]; continue;
    }
    if (!tailKw && tailClauseAt(kws, k, source, options)) { tailKw = kws[k]; continue; }
    if (!setOpKw && isSetOp(u, options.engine)) {
      setOpKw = kws[k];
      setOpUpper = u as SetOperatorKind;
      // After a set operator, anything following belongs to the next branch;
      // we do not look for trailing clause keywords here. ORDER BY / LIMIT
      // applied to the whole compound are owned by the LAST branch in v1.
      break;
    }
  }

  // Absolute end of this branch: first set-op or the input end.
  const branchEnd = setOpKw ? setOpKw.start : end;
  // Defensive: clause keyword orderings should be monotonic; if a later clause
  // appears before an earlier one (malformed SQL), drop the offending entries.
  // This keeps downstream parsers from chewing past the branch.
  const offsets: TopKeyword[] = [];
  if (fromKw) offsets.push(fromKw);
  if (whereKw) offsets.push(whereKw);
  if (groupKw) offsets.push(groupKw);
  if (havingKw) offsets.push(havingKw);
  if (windowKw) offsets.push(windowKw);
  if (orderKw) offsets.push(orderKw);
  if (limitKw) offsets.push(limitKw);
  for (let k = 1; k < offsets.length; k++) {
    if (offsets[k].start < offsets[k - 1].start) {
      // Out-of-order; clear the offending and everything after.
      const dropFrom = offsets[k];
      if (whereKw && whereKw.start >= dropFrom.start) whereKw = undefined;
      if (groupKw && groupKw.start >= dropFrom.start) groupKw = undefined;
      if (havingKw && havingKw.start >= dropFrom.start) havingKw = undefined;
      if (windowKw && windowKw.start >= dropFrom.start) windowKw = undefined;
      if (orderKw && orderKw.start >= dropFrom.start) orderKw = undefined;
      if (limitKw && limitKw.start >= dropFrom.start) limitKw = undefined;
      break;
    }
  }
  // Constrain branchEnd to be at least past the last clause keyword's start.
  void branchEnd;
  void CLAUSE_KEYWORDS;

  return {
    selectKw, distinct, all, top, into, fromKw, whereKw, groupKw, havingKw, windowKw, orderKw, limitKw, tailKw, setOpKw, setOpUpper,
  };
}

// Compute the byte range [start, end) of a clause given its keyword and the
// next clause keyword (whichever comes first). When there is no next clause,
// the range stops at `branchEnd`.
export function clauseRange(
  clauses: SelectClauseRanges,
  current: TopKeyword,
  branchEnd: number,
): { contentStart: number; contentEnd: number } {
  const candidates: number[] = [];
  const push = (k?: TopKeyword): void => {
    if (k && k.start > current.start) candidates.push(k.start);
  };
  push(clauses.fromKw);
  push(clauses.whereKw);
  push(clauses.groupKw);
  push(clauses.havingKw);
  push(clauses.windowKw);
  push(clauses.orderKw);
  push(clauses.limitKw);
  push(clauses.tailKw);
  push(clauses.setOpKw);
  const next = candidates.length ? Math.min(...candidates) : branchEnd;
  return { contentStart: current.end, contentEnd: next };
}

// Compute the byte range of the select-list content: from after SELECT [DISTINCT|ALL|TOP n]
// to the first of {INTO, FROM, WHERE, …, set-op, branchEnd}. The TOP-arg expression
// (e.g. `TOP 10`, `TOP (SELECT n FROM cfg)`, `TOP 10 PERCENT`) is NOT part of the
// select-list — we trim it by skipping forward from `top.end` past one expression.
export function selectListRange(
  source: string,
  options: ParseOptions,
  clauses: SelectClauseRanges,
  branchEnd: number,
): { contentStart: number; contentEnd: number } {
  const head = clauses.top ?? clauses.distinct ?? clauses.all ?? clauses.selectKw;
  const start = clauses.top
    ? skipTopArgument(source, options, clauses.top.end, branchEnd)
    : head.end;
  const candidates: number[] = [];
  const push = (k?: TopKeyword): void => {
    if (k && k.start > head.start) candidates.push(k.start);
  };
  push(clauses.into);
  push(clauses.fromKw);
  push(clauses.whereKw);
  push(clauses.groupKw);
  push(clauses.havingKw);
  push(clauses.orderKw);
  push(clauses.limitKw);
  push(clauses.tailKw);
  push(clauses.setOpKw);
  const next = candidates.length ? Math.min(...candidates) : branchEnd;
  return { contentStart: start, contentEnd: next };
}

// Skip past the TOP argument: either a parenthesised expression `TOP (n)` or a
// numeric literal `TOP 10` optionally followed by `PERCENT` and `WITH TIES`.
// eslint-disable-next-line complexity
function skipTopArgument(
  source: string,
  options: ParseOptions,
  start: number,
  end: number,
): number {
  void options;
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  if (source[i] === '(') {
    // Skip balanced parens.
    let d = 1; i++;
    while (i < end && d > 0) {
      if (source[i] === '(') d++;
      else if (source[i] === ')') d--;
      i++;
    }
  } else {
    // Skip a numeric literal (possibly with decimal point).
    while (i < end && /[0-9.]/.test(source[i])) i++;
  }
  // Optional PERCENT / WITH TIES — consume identifier-y tokens.
  while (i < end) {
    while (i < end && /\s/.test(source[i])) i++;
    if (!/[A-Za-z_]/.test(source[i] ?? '')) break;
    let j = i + 1;
    while (j < end && /[A-Za-z0-9_]/.test(source[j])) j++;
    const word = source.slice(i, j).toUpperCase();
    if (word === 'PERCENT' || word === 'WITH' || word === 'TIES') { i = j; continue; }
    break;
  }
  return i;
}
